import * as React from 'react';
import { styled } from '@linaria/react';

import type { Command } from './SelfDrawingPath.types';

interface Props {
  time: number;
  totalTime: number;
  commands: Array<Command>;
  highlightedCommandIndex: number | null;
  status: 'playing' | 'idle';
  handleScrub: (value: number) => void;
  handleTogglePlay: () => void;
}

function Timeline({
  time,
  totalTime,
  commands,
  highlightedCommandIndex,
  status,
  handleScrub,
  handleTogglePlay,
}: Props) {
  return (
    <Wrapper>
      <TimelineTrack>
        <CommandBlocks>
          {commands.map((command, index) => (
            <CommandBlock
              key={index}
              data-command-type={command.type}
              data-is-playing={status === 'playing'}
              data-is-selected={index === highlightedCommandIndex}
              style={{ flex: command.duration }}
            />
          ))}
        </CommandBlocks>
        <CommandLabels>
          {commands.map((command, index) => (
            <CommandLabel
              key={index}
              data-command-type={command.type}
              data-is-selected={index === highlightedCommandIndex}
              style={{ flex: command.duration }}
            >
              <Long>{command.type}</Long>
              <Short>{command.type.slice(0, 1)}</Short>
            </CommandLabel>
          ))}
        </CommandLabels>

        <TimelineHandle
          style={{ '--progress': `${(time / totalTime) * 100}%` }}
        />
        <RangeInput
          type="range"
          min={0}
          max={totalTime}
          step={10}
          value={time}
          onChange={(e) => handleScrub(Number(e.target.value))}
          onKeyDown={(event) => {
            if (event.key === ' ') {
              event.preventDefault();
            }
          }}
          onKeyUp={(event) => {
            if (event.key === ' ' || event.key === 'Enter') {
              event.preventDefault();
              handleTogglePlay();
            }
          }}
        />
      </TimelineTrack>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  --timeline-color: hsl(210deg 15% 90%);
  --block-height: 24px;
  --spring: cubic-bezier(0.135, 1.05, 0, 1.008);

  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  isolation: isolate;
`;

const TimelineTrack = styled.div`
  position: relative;
  height: calc(var(--block-height) + 8px);
  display: flex;
  align-items: center;

  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    height: var(--block-height);
    width: 2px;
    margin-block: auto;
    background: var(--timeline-color);
    border-radius: 2px;
  }

  &::before {
    left: 0;
  }

  &::after {
    right: 0;
  }
`;

const TimelineHandle = styled.div`
  position: absolute;
  z-index: 2;
  left: var(--progress);
  top: 0;
  bottom: 0;
  width: 8px;
  height: 100%;
  margin: auto;
  background: var(--color-text);
  transform: translateX(-50%);
  border-radius: 100px;
  border: 2px solid var(--color-background);
  pointer-events: none;

  ${Wrapper}:has(input:active, input:focus-visible) & {
    outline: 6px solid hsl(0deg 0% 100% / 0.25);

    &::before {
      content: '';
      position: absolute;
      inset: -4px;
      border: 2px solid hsl(45deg 100% 60%);
      border-radius: 100px;
    }
  }
`;

const RangeInput = styled.input`
  --overflow-left: 7px;
  --overflow-right: 7px;
  position: absolute;
  z-index: 100;
  inset: 0;
  left: calc(-1 * var(--overflow-left));
  right: calc(-1 * var(--overflow-right));
  width: calc(100% + var(--overflow-left) + var(--overflow-right));
  height: 100%;
  margin: 0;
  border: none;
  opacity: 0;
  cursor: pointer;
  -webkit-appearance: none;
  appearance: none;

  &::-webkit-slider-thumb {
    cursor: grab;
  }

  &:active {
    cursor: grabbing;
  }
  &:active::-webkit-slider-thumb {
    cursor: grabbing;
  }
`;

const CommandBlocks = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 0 4px;
`;

const CommandBlock = styled.div`
  --stripe-light-color: hsl(210deg 20% 40% / 0.5);
  --stripe-dark-color: hsl(210deg 10% 20% / 0.5);
  --animation-scroll-amount: 22.5px;
  flex: 1;
  position: relative;
  border: 2px solid;
  border-radius: 4px;
  height: 0px;
  /* Clamp the pseudo-element that causes the background to shift slightly */
  overflow: hidden;
  transition: all 1200ms var(--spring);

  &[data-is-selected='true'] {
    height: var(--block-height);
  }

  &[data-command-type='line'] {
    --stripe-light-color: hsl(250deg 100% 80% / 0.5);
    --stripe-dark-color: hsl(250deg 60% 60% / 0.5);
    border-color: hsl(250deg 100% 75%);
  }

  @keyframes scroll {
    from {
      transform: translateX(0);
    }
    to {
      transform: translateX(calc(var(--animation-scroll-amount)));
    }
  }

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    left: -32px;
    background: repeating-linear-gradient(
      45deg,
      var(--stripe-light-color) 0px 4px,
      var(--stripe-dark-color) 4px 8px
    );
    animation: scroll 1000ms linear infinite;
  }

  &[data-is-playing='false']::before {
    animation-play-state: paused;
  }
`;

const CommandLabels = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  gap: 4px;
  padding-inline: 4px;
`;

const CommandLabel = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  height: var(--block-height);
  font-size: 0.875rem;
  font-family: var(--font-family-mono);
  color: var(--color-gray-600);
  text-transform: uppercase;
  font-weight: var(--font-weight-medium);
  letter-spacing: 1px;
  transform: translateY(24px) scaleY(0);
  transform-origin: center top;
  opacity: 0;
  transition: all 500ms var(--spring);

  &[data-command-type='line'] {
    color: hsl(250deg 100% 75%);
  }

  &[data-is-selected='true'] {
    opacity: 1;
    transform: translateY(28px);
    transition: all 1000ms 150ms var(--spring);
  }
`;

const Long = styled.span`
  display: none;

  @media (min-width: 500px) {
    display: block;
  }
`;

const Short = styled.span`
  display: block;

  @media (min-width: 500px) {
    display: none;
  }
`;

export default Timeline;
